import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { MapPin, Play, Square, Camera, Briefcase, Home, Store, Wheat, UserPlus, PlusCircle, Pause, RotateCcw, Clock } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "../lib/auth";
import { FarmerAvatar } from "./FarmersPage";
import { fmtTimeIST } from "../lib/datefmt";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Compress image to <=1024px JPEG b64.
const fileToCompressedB64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read image"));
      img.onload = () => {
        const maxEdge = 1024;
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const getCoords = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  });

export default function DailyReportPage() {
  const { user } = useAuth();
  const isTeamLead = ["admin", "director", "manager", "rm_and_above", "sales_manager"].includes(user?.role);
  const [today, setToday] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(() => searchParams.get("session") || "");
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    const [t, s, c, f] = await Promise.all([
      api.get("/daily-report/today"),
      api.get("/daily-report/sessions"),
      api.get("/customers"),
      api.get("/farmers"),
    ]);
    setToday(t.data || null);
    setSessions(s.data || []);
    setCustomers(c.data || []);
    setFarmers(f.data || []);
    if (!selectedId && s.data?.length) {
      const list = s.data;
      const firstField = list.find((x) => x.mode === "field_day");
      setSelectedId((firstField || list[0]).id);
    }
    // Team leads: fetch users so we can offer a "Working with" dropdown of descendants.
    if (isTeamLead && ["sales_manager", "rm_and_above"].includes(user?.role)) {
      try {
        const perf = await api.get("/team/performance");
        setTeamMembers((perf.data.members || []).map((m) => ({ id: m.user_id, name: m.user_name, role: m.role })));
      } catch (err) {
        // Not every team lead can hit /team/performance — the dropdown just stays empty.
        logger.debug("team/performance not available:", err?.message);
      }
    }
  };

  const loadSelected = async (id) => {
    if (!id) return;
    const { data } = await api.get(`/gps/sessions/${id}/points`);
    setSelectedMeta(data.session);
    setSelectedPoints(data.points);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) loadSelected(selectedId);
  }, [selectedId]);

  // 5-min auto GPS poll while a field day is active.
  useEffect(() => {
    if (!today || today.mode !== "field_day" || today.status !== "active") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const tick = async () => {
      const c = await getCoords();
      if (!c) return;
      try {
        await api.post("/gps/points", { lat: c.lat, lng: c.lng });
        if (selectedId === today.id) loadSelected(today.id);
      } catch (e) {
        logger.error("auto GPS post failed:", e);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 5 * 60 * 1000);
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today?.id, today?.status]);

  return (
    <div>
      <PageHeader
        title="Daily Report"
        subtitle="Log your day — Field visits, meetings or work from home"
      />
      {today == null && <StartCard onStarted={load} teamMembers={teamMembers} />}
      {today?.mode === "field_day" && today.status === "active" && (
        <FieldDayActive
          today={today}
          customers={customers}
          farmers={farmers}
          onChange={load}
          onFarmersChange={async () => {
            const f = await api.get("/farmers");
            setFarmers(f.data || []);
          }}
        />
      )}
      {today && today.status === "ended" && <TodaySummary today={today} />}

      <SessionHistory
        sessions={sessions}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        selectedMeta={selectedMeta}
        selectedPoints={selectedPoints}
      />
    </div>
  );
}

function StartCard({ onStarted, teamMembers = [] }) {
  const [mode, setMode] = useState(null);
  const [startKm, setStartKm] = useState("");
  const [photoB64, setPhotoB64] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [achievement, setAchievement] = useState("");
  const [workingWith, setWorkingWith] = useState("");
  const [busy, setBusy] = useState(false);

  const onPhoto = async (f) => {
    if (!f) return;
    const b = await fileToCompressedB64(f);
    setPhotoB64(b);
    setPhotoName(f.name);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { mode };
      if (workingWith) payload.working_with_user_id = workingWith;
      if (mode === "field_day") {
        if (!startKm) return toast.error("Enter start km reading");
        if (!photoB64) return toast.error("Attach odometer photo");
        payload.start_km = Number(startKm);
        payload.start_km_photo_b64 = photoB64;
        const c = await getCoords();
        if (c) {
          payload.lat = c.lat;
          payload.lng = c.lng;
        }
      } else if (mode === "meeting") {
        payload.remarks = remarks;
      } else if (mode === "work_from_home") {
        payload.achievement = achievement;
        payload.remarks = remarks;
      }
      await api.post("/daily-report/start", payload);
      toast.success("Daily report started");
      onStarted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to start");
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <Card className="mb-6" data-testid="mode-picker">
        <h3 className="font-heading text-xl font-bold mb-4">How would you like to log today?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ModeCard icon={Briefcase} title="Market Visit" desc="Start day → visits → end day" onClick={() => setMode("field_day")} testid="mode-field-day" />
          <ModeCard icon={UserPlus} title="Meeting" desc="Attend a meeting — capture remarks" onClick={() => setMode("meeting")} testid="mode-meeting" />
          <ModeCard icon={Home} title="Work from Home" desc="Log achievement and remarks" onClick={() => setMode("work_from_home")} testid="mode-wfh" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid={`start-form-${mode}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">
          Start — {mode === "field_day" ? "Market Visit" : mode === "meeting" ? "Meeting" : "Work from Home"}
        </h3>
        <button className="text-xs text-[#5C6B5E] hover:text-[#1E3F20]" onClick={() => setMode(null)}>
          Change
        </button>
      </div>
      <div className="space-y-4">
        {mode === "field_day" && teamMembers.length > 0 && (
          <div>
            <Label>Working with (optional)</Label>
            <Select value={workingWith} onChange={(e) => setWorkingWith(e.target.value)} data-testid="working-with">
              <option value="">— Solo —</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({(m.role || "").replace(/_/g, " ")})
                </option>
              ))}
            </Select>
            <div className="text-xs text-[#5C6B5E] mt-1">Pick a team member to shadow — their dealers will show up when adding visits.</div>
          </div>
        )}
        {mode === "field_day" && (
          <>
            <div>
              <Label>Start km reading</Label>
              <Input
                type="number"
                step="0.1"
                value={startKm}
                onChange={(e) => setStartKm(e.target.value)}
                placeholder="e.g. 50000"
                data-testid="start-km"
              />
            </div>
            <div>
              <Label>Odometer photo (GPS location will be captured)</Label>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
                <Camera className="w-4 h-4" /> {photoName ? "Replace photo" : "Attach photo"}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="start-km-photo"
                  onChange={(e) => onPhoto(e.target.files?.[0])}
                />
              </label>
              {photoName && <div className="text-xs text-[#5C6B5E] mt-1">{photoName}</div>}
            </div>
          </>
        )}
        {mode === "meeting" && (
          <div>
            <Label>Meeting remarks</Label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={4}
              placeholder="What was discussed, who attended, outcomes…"
              data-testid="meeting-remarks"
              className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
            />
          </div>
        )}
        {mode === "work_from_home" && (
          <>
            <div>
              <Label>Achievement today</Label>
              <textarea
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                rows={3}
                placeholder="Calls made, deals closed, follow-ups completed…"
                data-testid="wfh-achievement"
                className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
              />
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                data-testid="wfh-remarks"
                className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
              />
            </div>
          </>
        )}
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="start-submit">
            <Play className="w-4 h-4" /> {busy ? "Saving…" : mode === "field_day" ? "Start Day" : "Log entry"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ModeCard({ icon: Icon, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-xl border border-[#E5E2DC] hover:border-[#1E3F20] hover:shadow-md transition-all p-5 bg-white"
    >
      <div className="w-11 h-11 rounded-lg bg-[#1E3F20]/10 grid place-items-center text-[#1E3F20] mb-3">
        <Icon className="w-5 h-5" strokeWidth={2.25} />
      </div>
      <div className="font-heading text-lg font-bold text-[#1A2E1C]">{title}</div>
      <div className="text-sm text-[#5C6B5E] mt-1">{desc}</div>
    </button>
  );
}

function FieldDayActive({ today, customers, farmers, onChange, onFarmersChange }) {
  const [visitOpen, setVisitOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [scopedCustomers, setScopedCustomers] = useState(customers);

  useEffect(() => {
    // If manager picked a sub-person to shadow, load THAT user's dealers.
    let cancelled = false;
    (async () => {
      if (today?.working_with_user_id) {
        try {
          const { data } = await api.get(`/customers?for_user_id=${today.working_with_user_id}`);
          if (!cancelled) setScopedCustomers(data);
        } catch {
          if (!cancelled) setScopedCustomers(customers);
        }
      } else {
        setScopedCustomers(customers);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today?.working_with_user_id, customers.length]);

  return (
    <>
      <Card className="mb-6" data-testid="field-day-active">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">Market Visit — Active</div>
            <div className="font-heading text-2xl font-black text-[#1A2E1C] mt-1">
              Start km: {today.start_km}
            </div>
            <div className="text-xs text-[#5C6B5E] mt-1">
              Started at {fmtTimeIST(today.start_time)} IST · GPS auto-captured every 5 min
            </div>
            {today.working_with_user_name && (
              <div className="text-xs mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] font-semibold" data-testid="working-with-badge">
                Working with: {today.working_with_user_name}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setVisitOpen(true)} data-testid="add-visit-btn">
              <PlusCircle className="w-4 h-4" /> Add visit
            </Button>
            <Button onClick={() => setEndOpen(true)} data-testid="end-day-btn">
              <Square className="w-4 h-4" /> End day
            </Button>
          </div>
        </div>
      </Card>

      {visitOpen && (
        <AddVisitModal
          customers={scopedCustomers}
          farmers={farmers}
          onClose={() => setVisitOpen(false)}
          onSaved={() => {
            setVisitOpen(false);
            onChange();
            onFarmersChange();
          }}
        />
      )}
      {endOpen && (
        <EndDayForm
          startKm={today.start_km}
          onClose={() => setEndOpen(false)}
          onEnded={() => {
            setEndOpen(false);
            onChange();
          }}
        />
      )}
    </>
  );
}

function AddVisitModal({ customers, farmers, onClose, onSaved }) {
  const [kind, setKind] = useState("dealer");
  const [customerId, setCustomerId] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [newName, setNewName] = useState("");
  const [newMobile, setNewMobile] = useState("");
  const [newVillage, setNewVillage] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photoB64, setPhotoB64] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [busy, setBusy] = useState(false);

  const onPhoto = async (f) => {
    if (!f) return;
    setPhotoB64(await fileToCompressedB64(f));
    setPhotoName(f.name);
  };

  const submit = async () => {
    setBusy(true);
    const c = await getCoords();
    if (!c) {
      setBusy(false);
      return toast.error("Location not available");
    }
    const payload = {
      kind,
      lat: c.lat,
      lng: c.lng,
      remarks,
      photo_b64: photoB64 || null,
    };
    if (kind === "dealer") payload.customer_id = customerId;
    if (kind === "farmer") payload.farmer_id = farmerId;
    if (kind === "new_lead" || kind === "new_farmer") {
      payload.new_name = newName;
      payload.new_mobile = newMobile;
      payload.new_village = newVillage;
    }
    try {
      const r = await api.post("/daily-report/visit", payload);
      if (r?.data?.queued) toast.info("Offline — visit queued. Will sync when back online.");
      else toast.success("Visit logged");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to log visit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="add-visit-modal">
        <div className="p-5 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="font-heading text-xl font-bold">Add visit</div>
          <button onClick={onClose} className="text-[#5C6B5E]">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { v: "dealer", l: "Dealer", i: Store },
                { v: "farmer", l: "Farmer", i: Wheat },
                { v: "new_lead", l: "New Lead", i: UserPlus },
                { v: "new_farmer", l: "New Farmer", i: PlusCircle },
              ].map(({ v, l, i: I }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setKind(v)}
                  data-testid={`visit-kind-${v}`}
                  className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-1.5 justify-center font-semibold transition-colors ${
                    kind === v ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "border-[#E5E2DC] text-[#1A2E1C] hover:border-[#1E3F20]"
                  }`}
                >
                  <I className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>
          </div>
          {kind === "dealer" && (
            <div>
              <Label>Dealer</Label>
              <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="visit-dealer">
                <option value="">Select…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </div>
          )}
          {kind === "farmer" && (
            <div>
              <Label>Farmer</Label>
              <Select value={farmerId} onChange={(e) => setFarmerId(e.target.value)} data-testid="visit-farmer">
                <option value="">Select…</option>
                {farmers.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}{f.village ? ` — ${f.village}` : ""}
                  </option>
                ))}
              </Select>
              {farmerId && (() => {
                const f = farmers.find((x) => x.id === farmerId);
                if (!f) return null;
                return (
                  <div className="mt-3 flex items-center gap-3 p-3 rounded-lg bg-[#F9F8F6] border border-[#E5E2DC]" data-testid="visit-farmer-preview">
                    <FarmerAvatar path={f.photo_path} name={f.name} size={48} />
                    <div className="text-sm">
                      <div className="font-semibold text-[#1A2E1C]">{f.name}</div>
                      {f.village && <div className="text-xs text-[#5C6B5E]">{f.village}</div>}
                      {f.mobile && <div className="text-xs text-[#5C6B5E] font-mono">{f.mobile}</div>}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {(kind === "new_lead" || kind === "new_farmer") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{kind === "new_lead" ? "Lead name" : "Farmer name"}</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="visit-new-name" />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input value={newMobile} onChange={(e) => setNewMobile(e.target.value)} data-testid="visit-new-mobile" />
              </div>
              {kind === "new_farmer" && (
                <div className="sm:col-span-2">
                  <Label>Village</Label>
                  <Input value={newVillage} onChange={(e) => setNewVillage(e.target.value)} data-testid="visit-new-village" />
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Remarks</Label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              data-testid="visit-remarks"
              className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
            />
          </div>
          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
              <Camera className="w-4 h-4" /> {photoName ? "Replace photo" : "Attach photo (optional)"}
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="visit-photo" onChange={(e) => onPhoto(e.target.files?.[0])} />
            </label>
            {photoName && <div className="text-xs text-[#5C6B5E] mt-1">{photoName}</div>}
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="visit-submit">
            {busy ? "Saving…" : "Log visit"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EndDayForm({ startKm, onClose, onEnded }) {
  const [endKm, setEndKm] = useState("");
  const [photoB64, setPhotoB64] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [busy, setBusy] = useState(false);
  const total = endKm ? Math.max(0, Number(endKm) - Number(startKm)) : 0;

  const onPhoto = async (f) => {
    if (!f) return;
    setPhotoB64(await fileToCompressedB64(f));
    setPhotoName(f.name);
  };

  const submit = async () => {
    if (!endKm) return toast.error("Enter end km reading");
    if (!photoB64) return toast.error("Attach odometer photo");
    setBusy(true);
    const c = await getCoords();
    const payload = { end_km: Number(endKm), end_km_photo_b64: photoB64 };
    if (c) {
      payload.lat = c.lat;
      payload.lng = c.lng;
    }
    try {
      await api.post("/daily-report/end", payload);
      toast.success(`Day ended — total ${total} km`);
      onEnded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to end day");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="end-day-modal">
        <div className="p-5 border-b border-[#E5E2DC] font-heading text-xl font-bold">End Market Visit</div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#5C6B5E]">Start km was <b className="text-[#1A2E1C]">{startKm}</b></div>
          <div>
            <Label>End km reading</Label>
            <Input
              type="number"
              step="0.1"
              value={endKm}
              onChange={(e) => setEndKm(e.target.value)}
              placeholder="e.g. 50045"
              data-testid="end-km"
            />
          </div>
          <div>
            <Label>Odometer photo</Label>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
              <Camera className="w-4 h-4" /> {photoName ? "Replace photo" : "Attach photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="end-km-photo" onChange={(e) => onPhoto(e.target.files?.[0])} />
            </label>
            {photoName && <div className="text-xs text-[#5C6B5E] mt-1">{photoName}</div>}
          </div>
          <div className="text-sm">
            Total km today: <b className="font-heading text-xl text-[#1E3F20]" data-testid="total-km-preview">{total}</b>
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="end-submit">
            {busy ? "Saving…" : "End day"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TodaySummary({ today }) {
  const modeLabel = today.mode === "field_day" ? "Market Visit" : today.mode === "meeting" ? "Meeting" : "Work from Home";
  const showExpenseCTA = today.mode === "field_day" && !today.expense_status;
  return (
    <Card className="mb-6" data-testid="today-summary">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">Today · {modeLabel}</div>
      <div className="font-heading text-xl font-bold text-[#1A2E1C] mt-1">
        {today.mode === "field_day" ? `${today.km_covered || 0} km covered` : "Logged"}
      </div>
      {today.mode === "field_day" && (
        <div className="text-sm text-[#5C6B5E] mt-1">
          Start {today.start_km} → End {today.end_km} · {today.start_time?.slice(11, 16)} to {today.end_time?.slice(11, 16)}
        </div>
      )}
      {today.remarks && <div className="text-sm mt-2"><b>Remarks:</b> {today.remarks}</div>}
      {today.achievement && <div className="text-sm mt-1"><b>Achievement:</b> {today.achievement}</div>}
      {showExpenseCTA && (
        <a
          href="/expenses/mine"
          data-testid="submit-expenses-cta"
          className="inline-flex items-center gap-2 mt-3 bg-[#C15A2C] text-white hover:bg-[#A64A22] rounded-lg px-4 py-2 font-semibold text-sm transition-colors"
        >
          Submit today's expenses →
        </a>
      )}
      {today.expense_status && (
        <div className="text-xs mt-3">
          Expenses: <b>{today.expense_status}</b>
          {today.expense_total ? ` · ₹${Number(today.expense_total).toFixed(0)}` : ""}
        </div>
      )}
    </Card>
  );
}

function SessionHistory({ sessions, selectedId, setSelectedId, selectedMeta, selectedPoints }) {
  const points = (selectedPoints || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
  const center = points.length ? [points[0].lat, points[0].lng] : [28.6, 77.2];
  // Playback state lives here so both the control bar and the moving marker inside <MapContainer> can share it.
  const [progress, setProgress] = useState(0); // 0..1
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10); // playback speed multiplier

  // Reset playback whenever the selected session changes.
  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [selectedId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-0 overflow-hidden lg:col-span-1" data-testid="sessions-list">
        <div className="p-4 border-b border-[#E5E2DC] font-heading text-lg font-bold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#1E3F20]" /> History
        </div>
        {sessions.length === 0 ? (
          <div className="p-6">
            <EmptyState title="No sessions" description="Your daily reports will appear here." />
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                data-testid={`session-${s.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60 ${
                  selectedId === s.id ? "bg-[#F2EDE4]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#1E3F20]">
                    {(s.mode || "field_day").replace(/_/g, " ")}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#5C6B5E]">{s.status}</span>
                </div>
                <div className="text-sm mt-1">{s.date_key || (s.start_time || "").slice(0, 10)}</div>
                {s.mode === "field_day" && s.km_covered != null && (
                  <div className="text-xs text-[#5C6B5E] mt-0.5">{s.km_covered} km</div>
                )}
                {s.user_name && <div className="text-xs text-[#5C6B5E] mt-0.5">{s.user_name}</div>}
              </button>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-0 overflow-hidden lg:col-span-2">
        <div className="p-4 border-b border-[#E5E2DC] font-heading text-lg font-bold flex items-center justify-between">
          <span>{selectedMeta ? `${(selectedMeta.mode || "field_day").replace(/_/g, " ")} · ${selectedMeta.date_key || ""}` : "Select a session"}</span>
          {selectedMeta?.mode === "field_day" && (
            <button
              onClick={async () => {
                try {
                  const res = await api.get(`/daily-report/sessions/${selectedMeta.id}/route.kml`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `route_${selectedMeta.date_key || selectedMeta.id}.kml`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("KML download failed");
                }
              }}
              data-testid="download-kml"
              className="text-xs px-3 py-1.5 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
            >
              <MapPin className="w-3.5 h-3.5" /> Download route (KML)
            </button>
          )}
        </div>
        {points.length > 0 ? (
          <>
            {points.length >= 2 && (
              <RoutePlayback
                points={points}
                progress={progress}
                setProgress={setProgress}
                playing={playing}
                setPlaying={setPlaying}
                speed={speed}
                setSpeed={setSpeed}
              />
            )}
            <div className="h-[420px]">
              <MapContainer center={center} zoom={13} className="h-full w-full" scrollWheelZoom>
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Polyline positions={points.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#1E3F20", weight: 3 }} />
                {points.map((p, i) => (
                  <Marker key={p.id || i} position={[p.lat, p.lng]}>
                    <Popup>
                      <div className="text-xs">
                        <div><b>{p.note || "GPS point"}</b></div>
                        {p.remarks && <div className="mt-1">{p.remarks}</div>}
                        <div className="text-[10px] text-[#5C6B5E] mt-1">{p.timestamp?.slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {points.length >= 2 && <PlaybackMarker points={points} progress={progress} />}
              </MapContainer>
            </div>
          </>
        ) : (
          <div className="p-8" data-testid="route-empty-state">
            {selectedMeta && selectedMeta.mode !== "field_day" ? (
              <EmptyState
                title="No route for this entry"
                description={`${selectedMeta.mode === "meeting" ? "Meeting" : "Work-from-home"} days are not GPS-tracked. Pick a Field Day from History to see its map and route playback.`}
              />
            ) : (
              <EmptyState title="No points recorded" description="This session has no GPS points yet." />
            )}
          </div>
        )}
      </Card>
    </div>
  );
}


const SPEED_OPTIONS = [1, 2, 5, 10, 20, 50, 100];

// Compute the animated position by linear interpolation between the two
// segment endpoints selected by `progress` (0..1).
function positionAt(points, progress) {
  if (!points.length) return null;
  if (points.length === 1) return [points[0].lat, points[0].lng];
  const clamped = Math.max(0, Math.min(1, progress));
  const scaled = clamped * (points.length - 1);
  const i = Math.floor(scaled);
  const t = scaled - i;
  const a = points[i];
  const b = points[Math.min(points.length - 1, i + 1)];
  return [a.lat + (b.lat - a.lat) * t, a.lng + (b.lng - a.lng) * t];
}

// Return the last point index at or before this progress — used to display "Now near <label>".
function currentIndex(points, progress) {
  return Math.min(points.length - 1, Math.floor(progress * (points.length - 1)));
}

// Interpolated wall-clock time (ms) at `progress`, so the readout moves smoothly between points.
function timeAt(points, progress) {
  if (!points.length) return null;
  const clamped = Math.max(0, Math.min(1, progress));
  const scaled = clamped * (points.length - 1);
  const i = Math.floor(scaled);
  const t = scaled - i;
  const a = new Date(points[i]?.timestamp).getTime();
  const b = new Date(points[Math.min(points.length - 1, i + 1)]?.timestamp).getTime();
  if (Number.isNaN(a)) return null;
  if (Number.isNaN(b) || b < a) return a;
  return a + (b - a) * t;
}

function fmtElapsed(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
}

function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso.slice(11, 16);
  }
}

function RoutePlayback({ points, progress, setProgress, playing, setPlaying, speed, setSpeed }) {
  // Total duration used to scale playback. Fallback to 1 hour if timestamps aren't reliable.
  const totalSec = useMemo(() => {
    const first = points[0]?.timestamp;
    const last = points[points.length - 1]?.timestamp;
    if (!first || !last) return 3600;
    const d1 = new Date(first).getTime();
    const d2 = new Date(last).getTime();
    if (Number.isNaN(d1) || Number.isNaN(d2) || d2 <= d1) return 3600;
    return Math.max(60, (d2 - d1) / 1000);
  }, [points]);

  useEffect(() => {
    if (!playing) return;
    let rafId;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000; // seconds elapsed since last frame
      last = now;
      setProgress((p) => {
        const nextProgress = p + (dt * speed) / totalSec;
        if (nextProgress >= 1) {
          setPlaying(false);
          return 1;
        }
        return nextProgress;
      });
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [playing, speed, totalSec, setProgress, setPlaying]);

  const idx = currentIndex(points, progress);
  const current = points[idx] || {};
  const percent = Math.round(progress * 100);
  const nowMs = timeAt(points, progress);
  const startMs = new Date(points[0]?.timestamp).getTime();
  const endMs = new Date(points[points.length - 1]?.timestamp).getTime();
  const hasTimes = !Number.isNaN(startMs) && !Number.isNaN(endMs) && nowMs != null;

  return (
    <div className="p-3 border-b border-[#E5E2DC] bg-[#F9F8F6]" data-testid="route-playback">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            if (progress >= 1) setProgress(0);
            setPlaying((p) => !p);
          }}
          data-testid="playback-toggle"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[#1E3F20] text-white hover:bg-[#153217] font-semibold"
        >
          {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> {progress >= 1 ? "Replay" : "Play"}</>}
        </button>
        <button
          onClick={() => {
            setPlaying(false);
            setProgress(0);
          }}
          data-testid="playback-reset"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-white border border-[#E5E2DC] text-[#1E3F20] hover:bg-[#F2EDE4] font-semibold"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
        <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A2E1C]">
          <span className="text-[#5C6B5E] mr-1">Speed</span>
          {SPEED_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              data-testid={`playback-speed-${s}`}
              className={`px-1.5 py-0.5 rounded ${s === speed ? "bg-[#DDA15E] text-[#1A2E1C]" : "text-[#5C6B5E] hover:text-[#1A2E1C]"}`}
            >
              {s}×
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2" data-testid="playback-status">
          <div className="inline-flex items-center gap-1.5 bg-[#1E3F20] text-white rounded-md px-2.5 py-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="font-heading text-base font-bold leading-none tabular-nums" data-testid="playback-time">
              {hasTimes ? fmtTs(new Date(nowMs).toISOString()) : "—"}
            </span>
          </div>
          <div className="text-xs text-[#5C6B5E] leading-tight">
            <div>{current.note || "point"} · {percent}%</div>
            {hasTimes && <div className="tabular-nums">+{fmtElapsed(nowMs - startMs)} since start</div>}
          </div>
        </div>
      </div>
      <input
        type="range"
        min="0"
        max="1000"
        value={Math.round(progress * 1000)}
        onChange={(e) => {
          setPlaying(false);
          setProgress(Number(e.target.value) / 1000);
        }}
        data-testid="playback-slider"
        className="w-full mt-2 accent-[#1E3F20]"
      />
      {hasTimes && (
        <div className="flex justify-between text-[11px] text-[#5C6B5E] tabular-nums -mt-0.5" data-testid="playback-range-times">
          <span>Start {fmtTs(points[0].timestamp)}</span>
          <span>Total {fmtElapsed(endMs - startMs)}</span>
          <span>End {fmtTs(points[points.length - 1].timestamp)}</span>
        </div>
      )}
    </div>
  );
}

function PlaybackMarker({ points, progress }) {
  const pos = positionAt(points, progress);
  const nowMs = timeAt(points, progress);
  const label = nowMs != null ? fmtTs(new Date(nowMs).toISOString()) : "";
  const icon = useMemo(
    () =>
      L.divIcon({
        className: "playback-marker",
        html:
          '<div style="position:relative;width:18px;height:18px;border-radius:50%;background:#C15A2C;border:3px solid #fff;box-shadow:0 0 0 2px #C15A2C,0 6px 12px rgba(0,0,0,0.25)">' +
          (label
            ? `<div style="position:absolute;left:50%;bottom:22px;transform:translateX(-50%);white-space:nowrap;background:#1E3F20;color:#fff;font:700 12px/1 Inter,system-ui,sans-serif;padding:4px 7px;border-radius:6px;box-shadow:0 2px 6px rgba(0,0,0,.25)">${label}</div>`
            : "") +
          "</div>",
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      }),
    [label]
  );
  if (!pos) return null;
  return <Marker position={pos} icon={icon} interactive={false} />;
}
